/**
 * Login Page - Autenticación de usuarios
 * @module pages/LoginPage
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/useAuthStore'
import { Network, Mail, Lock, User, Building2, Eye, EyeOff, LogIn, UserPlus, Ticket } from 'lucide-react'

type FormMode = 'login' | 'register' | 'invite'

interface LoginPageProps {
  onSuccess?: () => void
}

export function LoginPage({ onSuccess }: LoginPageProps) {
  const [formMode, setFormMode] = useState<FormMode>('login')
  const [showPassword, setShowPassword] = useState(false)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [tenantSlug, setTenantSlug] = useState('default')
  const [inviteToken, setInviteToken] = useState('')

  const { login, register, acceptInvite, loading, error, clearError } = useAuthStore()

  useEffect(() => {
    const inv = new URLSearchParams(window.location.search).get('invite')
    if (inv) {
      setFormMode('invite')
      setInviteToken(inv)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()

    try {
      if (formMode === 'login') {
        await login(email, password, tenantSlug)
      } else if (formMode === 'register') {
        await register(email, password, name, tenantSlug)
      } else {
        await acceptInvite(inviteToken, password, name)
      }
      onSuccess?.()
    } catch {
      // Error en store
    }
  }

  const title =
    formMode === 'login'
      ? 'Iniciar Sesión'
      : formMode === 'register'
        ? 'Crear Cuenta'
        : 'Aceptar invitación'

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-2xl mb-4">
            <Network className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">NetEasyFTTX</h1>
          <p className="text-primary-200 mt-2">Sistema de Gestión de Red FTTH</p>
        </div>

        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-semibold text-center mb-6">{title}</h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {formMode === 'invite' && (
              <div>
                <label className="block text-sm font-medium mb-1.5">Token de invitación</label>
                <div className="relative">
                  <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={inviteToken}
                    onChange={(e) => setInviteToken(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-dark-600 rounded-lg dark:bg-dark-700"
                    required
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            {(formMode === 'register' || formMode === 'invite') && (
              <div>
                <label className="block text-sm font-medium mb-1.5">Nombre</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Tu nombre completo"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-dark-700"
                    required
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            {formMode !== 'invite' && (
              <div>
                <label className="block text-sm font-medium mb-1.5">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="correo@ejemplo.com"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-dark-700"
                    required
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1.5">Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-12 py-2.5 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-dark-700"
                  required
                  minLength={6}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {formMode !== 'invite' && (
              <div>
                <label className="block text-sm font-medium mb-1.5">Organización</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={tenantSlug}
                    onChange={(e) => setTenantSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                    placeholder="mi-empresa"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-dark-700"
                    required
                    disabled={loading}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {formMode === 'login' ? 'Slug de tu organización' : 'Se creará si no existe'}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 font-medium disabled:opacity-50"
            >
              {loading ? (
                <span className="animate-spin">⏳</span>
              ) : formMode === 'login' ? (
                <>
                  <LogIn className="w-5 h-5" />
                  Iniciar Sesión
                </>
              ) : formMode === 'register' ? (
                <>
                  <UserPlus className="w-5 h-5" />
                  Crear Cuenta
                </>
              ) : (
                <>
                  <Ticket className="w-5 h-5" />
                  Unirme
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center space-y-2 text-sm text-gray-600 dark:text-gray-400">
            {formMode === 'login' && (
              <p>
                ¿No tienes cuenta?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setFormMode('register')
                    clearError()
                  }}
                  className="text-primary-600 font-medium"
                  disabled={loading}
                >
                  Regístrate
                </button>
              </p>
            )}
            {formMode === 'register' && (
              <p>
                ¿Ya tienes cuenta?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setFormMode('login')
                    clearError()
                  }}
                  className="text-primary-600 font-medium"
                  disabled={loading}
                >
                  Inicia sesión
                </button>
              </p>
            )}
            {formMode === 'invite' && (
              <p>
                <button
                  type="button"
                  onClick={() => {
                    setFormMode('login')
                    clearError()
                  }}
                  className="text-primary-600 font-medium"
                  disabled={loading}
                >
                  Volver al inicio de sesión
                </button>
              </p>
            )}
          </div>
        </div>

        <p className="text-center text-primary-200 text-sm mt-6 flex flex-wrap justify-center gap-x-4 gap-y-1">
          <Link to="/about" className="hover:underline">
            Qué es NetEasyFTTX
          </Link>
          <Link to="/pricing" className="hover:underline">
            Planes y prueba 21 días
          </Link>
          <Link to="/soporte" className="hover:underline">
            Soporte
          </Link>
          <span>© NetEasyFTTX</span>
        </p>
      </div>
    </div>
  )
}
